import { Card } from "@/components/ui/card";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import {
  BellAlertIcon,
  BellIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { PopoverContent } from "@radix-ui/react-popover";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { JSX, useState, useMemo } from "react";
import { Locale } from "date-fns";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTranslation } from "react-i18next";
import { FetchNotificationsDocument, FetchNotificationsQuery, FetchNotificationsQueryVariables, Notification, NotificationType } from "@/graphql/__generated__/graphql";
import useUserStore from "@/store/shield";
import { getNotificationURL, hasNotificationEntity, processNotification } from "./utils";
import i18n from "../../../../../i18n";
import { enGB, de } from "date-fns/locale";
import { CircleLoader } from "../../loaders";
import NotificationCard from "./NotificationCard";
import useMarkNotificationsAsReadMutationHook from "@/hooks/mutations/useMarkNotificationsAsReadMutation";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useQuery } from "@apollo/client/react";

// Mapping between i18next language codes and date-fns locales
const localeMap: { [key: string]: Locale } = {
  en: enGB,
  de: de,
};

/**
 * NotificationCenter component
 *
 * This component displays a notification center with a bell icon. When the bell icon is clicked,
 * a popover appears showing a list of notifications. Users can mark notifications as read, clear all notifications,
 * or navigate to a specific page related to a notification.
 *
 * Hooks:
 * - `useAiImportStore`: Custom hook to manage notifications.
 * - `useNavigate`: Hook from `react-router-dom` to navigate programmatically.
 *
 * States:
 * - `open`: Boolean state to control the visibility of the popover.
 *
 * Constants:
 * - `hasNotifications`: Boolean indicating if there are any unread notifications.
 *
 * Handlers:
 * - `handleViewClick`: Navigates to the createTender page with the AIImport ID and marks the notification as read.
 *
 * Components:
 * - `ToolBarButton`: Button component for toolbar actions.
 *
 * UI Elements:
 * - Bell icon with a badge counter for unread notifications.
 * - Popover containing a list of notifications or a message indicating no notifications are available.
 * - Each notification card displays the title, message, and a "View" button to navigate to a specific page.
 *
 * @returns {JSX.Element} The Notification Center component.
 * @component
 * @example
 * return (
 *   <NotificationCenter />
 * )
 */
const NotificationCenter: React.FC = (): JSX.Element => {

  // Hooks
  const navigate = useNavigate();
  const { t } = useTranslation(["components"]);
  const orgId = useUserStore((state) => state.orgId);

  // States
  const [open, setOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("actionRequired");

  // Query hooks
  const { data, loading, refetch } = useQuery<FetchNotificationsQuery, FetchNotificationsQueryVariables>(FetchNotificationsDocument,{
    pollInterval: 15000,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: false // Prevents Apollo from updating the loading state during polling refetches
  });

  // Mutations
  const { markNotificationsAsReadMutation } = useMarkNotificationsAsReadMutationHook();

  /**
   * Handles the click event on a notification by navigating to its associated URL and processing
   * any chat mentions if applicable.
   *
   * @param {Notification} notification - The notification object that was clicked.
   * @returns {void}
   */
  const handleViewClick = (notification: Notification): void => {
    getNotificationURL(notification, navigate);
    // Close the popover after the notification is viewed
    setOpen(false);
  };

  /**
   * Marks a notification as read.
   *
   * @param {Notification} notification - The notification object to be marked as read.
   * @returns {void}
   */
  const handleMarkAsRead = (notification: Notification): void => {
    markNotificationsAsReadMutation({ variables: { notificationIds: [notification.id] } })
      .then(() => {
      // Refetch the notifications after the notification is marked as read
        refetch();
      });
  };

  /**
   * Handles tab changes.
   *
   * @param {string} value - The new active tab value
   * @returns {void}
   */
  const handleTabChange = (value: string): void => {
    setActiveTab(value);
  };

  // Notifications with a resolved entity
  const visibleNotifications = useMemo(
    () => (data?.fetchNotifications ?? []).filter(hasNotificationEntity) as Notification[],
    [data?.fetchNotifications]
  );

  /**
   * Marks all visible notifications as read across both tabs.
   *
   * Filters notifications to only include those that can be marked as read:
   * - All informative notifications (isActionRequired = false)
   * - Action-required notifications of type AiImportSuccessful
   *
   * @returns {void}
   */
  const handleMarkAllAsRead = (): void => {
    if (visibleNotifications.length === 0) return;

    const markableNotificationIds = visibleNotifications
      .filter((n) =>
        !n.isActionRequired || n.type === NotificationType.AiImportSuccessful
      )
      .map((n) => n.id);

    if (markableNotificationIds.length === 0) return;

    markNotificationsAsReadMutation({ variables: { notificationIds: markableNotificationIds } })
      .then(() => {
        refetch();
      });
  };

  // Group and sort visible notifications
  const groupedNotifications = useMemo(() => {
    const grouped = {
      actionRequired: [] as Notification[],
      informative: [] as Notification[]
    };

    visibleNotifications.forEach(notification => {
      if (notification.isActionRequired) {
        grouped.actionRequired.push(notification);
      } else {
        grouped.informative.push(notification);
      }
    });

    // Sort each group by creation date (newest first)
    grouped.actionRequired.sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    grouped.informative.sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    return grouped;
  }, [visibleNotifications]);

  // Memoized: Process all notifications once instead of on every render
  const processedNotifications = useMemo(() => {
    const processed = {
      actionRequired: groupedNotifications.actionRequired.map((notification) => ({
        notification,
        processed: processNotification(t, notification, orgId)
      })),
      informative: groupedNotifications.informative.map((notification) => ({
        notification,
        processed: processNotification(t, notification, orgId)
      }))
    };
    return processed;
  }, [groupedNotifications, t, orgId]);

  const notificationCount = visibleNotifications.length;
  const hasNotifications = notificationCount > 0;
  const NotificationIcon = hasNotifications ? BellAlertIcon : BellIcon;

  const primaryLanguage = i18n.language.split("-")[0]; // Extract primary language code
  const currentLocale: Locale = localeMap[primaryLanguage] || enGB;

  // Count notifications in each group
  const actionRequiredCount = groupedNotifications.actionRequired.length;
  const informativeCount = groupedNotifications.informative.length;

  return (
    <Popover data-cy="notification-center" open={open} onOpenChange={setOpen}>
      <PopoverTrigger data-cy="notification-center-trigger">
        <div className="relative group mx-1">
          <NotificationIcon data-cy="notification-center-bell-icon" className="icon-md text-gray-600 group-active:scale-95" />
          {/* Badge counter */}
          {hasNotifications && (
            <div className="notifications-badge">
              <p className="text-xs text-white">
                {notificationCount}
              </p>
            </div>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="pt-0 w-[450px]" sideOffset={14} align="end" data-cy="notification-center-content">
        <Card className="shadow-md w-[450px] px-4 py-4 z-50">
          {/* Loading state */}
          {loading ? (
            <div className="flex gap-2 items-center w-full justify-center">
              <CircleLoader visible={true} height={16} width={16} strokeWidth={2}/>
              <p className="text-gray-500 font-light">
                {t("notificationcenter.fetching")}
              </p>
            </div>
          ) : !hasNotifications ? (
            <p className="text-gray-500 text-sm text-center py-4">
              {t("notificationcenter.nodata")}
            </p>
          ) : (
            <>
              {/* Header */}
              <div className="mb-3 flex items-center justify-between border-b pb-3">
                <div className="flex items-center gap-2">
                  <h1 className="text-lg font-semibold">{t("notificationcenter.title")}</h1>
                  <Tooltip delayDuration={500}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        tabIndex={-1}
                        className="inline-flex h-5 w-5 items-center justify-center rounded-sm hover:bg-transparent"
                      >
                        <InformationCircleIcon className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[300px]">
                      <p className="text-sm">{t("notificationcenter.tooltip")}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>

                <div className="flex items-center gap-1">
                  {/* Mark All as Read Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleMarkAllAsRead}
                    disabled={!hasNotifications}
                  >
                    {t("notificationcenter.buttons.clearall")}
                  </Button>
                </div>
              </div>
              <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                <TabsList className="mb-3 grid w-full grid-cols-2">
                  <TabsTrigger value="actionRequired" className="relative">
                    <span className="truncate">{t("notificationcenter.actionrequired")}</span>
                    {actionRequiredCount > 0 && (
                      <span className="ml-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-medium text-white">
                        {actionRequiredCount}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="informative" className="relative">
                    <span className="truncate">{t("notificationcenter.informative")}</span>
                    {informativeCount > 0 && (
                      <span className="ml-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-foreground px-1.5 text-xs font-medium text-white">
                        {informativeCount}
                      </span>
                    )}
                  </TabsTrigger>
                </TabsList>

                {/* Action Required Tab */}
                <TabsContent value="actionRequired" className="mt-0">
                  {actionRequiredCount === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-8">
                      {t("notificationcenter.noactionrequired")}
                    </p>
                  ) : (
                    <ScrollArea className="h-[300px] pr-2">
                      <div className="space-y-2">
                        {processedNotifications.actionRequired.map(({ notification, processed }) => {
                          const showMarkAsRead = notification.type === NotificationType.AiImportSuccessful;
                          return (
                            <NotificationCard
                              key={notification.id}
                              notification={notification}
                              processedNotification={processed}
                              handleViewClick={handleViewClick}
                              handleMarkAsRead={showMarkAsRead ? handleMarkAsRead : undefined}
                              currentLocale={currentLocale}
                              t={t}
                            />
                          );
                        })}
                      </div>
                    </ScrollArea>
                  )}
                </TabsContent>

                {/* Informative Tab */}
                <TabsContent value="informative" className="mt-0">
                  {informativeCount === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-8">
                      {t("notificationcenter.noinformative")}
                    </p>
                  ) : (
                    <ScrollArea className="h-[300px] pr-2">
                      <div className="space-y-2">
                        {processedNotifications.informative.map(({ notification, processed }) => {
                          return (
                            <NotificationCard
                              key={notification.id}
                              notification={notification}
                              processedNotification={processed}
                              handleViewClick={handleViewClick}
                              handleMarkAsRead={handleMarkAsRead}
                              currentLocale={currentLocale}
                              t={t}
                            />
                          );
                        })}
                      </div>
                    </ScrollArea>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </Card>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationCenter;

/**
  * A button component used in the toolbar with a ghost variant and small size.
  *
  * @param onClick - The function to call when the button is clicked.
  * @param title - The text to display inside the button.
  * @param disabled - A boolean indicating whether the button is disabled.
  *
  * @returns {JSX.Element} A styled button component.
  */
export const ToolBarButton = ({ onClick, title, disabled }: { onClick: (_e: React.MouseEvent<HTMLButtonElement>) => void, title: string, disabled: boolean }): JSX.Element => (
  <Button
    variant={"ghost"}
    size={"xs"}
    onClick={onClick}
    disabled={disabled}
    className="cursor-pointer text-sm font-medium active:scale-95 st-animate-200 hover:bg-slate-800 hover:text-white"
  >
    {title}
  </Button>
);
